import dayjs from 'dayjs'
import {
  CreateTaskTrackerTaskFieldValue,
  TaskTrackerBoardCard,
  UpdateTaskTrackerTaskRequest,
} from '../store/taskTracker.types'
import { isTaskTrackerSystemDatetimeStickerFieldType } from './taskTrackerBoard.stickerFilters.utils'
import { areTaskPerformerIdsEqual } from './taskTrackerPerformers.utils'

export type TaskInfoFormState = {
  isCompleted: boolean
  performerIds: number[]
  deadline: string | null
}

export const formatTaskDeadline = (value: string | null) => {
  if (!value) return '—'

  const date = dayjs(value)
  if (!date.isValid()) return '—'

  return date.format('DD.MM.YYYY')
}

export const normalizeTaskDeadline = (value: string | null | undefined) => {
  if (!value?.trim()) return null

  const date = dayjs(value)
  if (!date.isValid()) return null

  return date.format('YYYY-MM-DD')
}

export const mapTaskToInfoFormState = (
  task: TaskTrackerBoardCard | null,
): TaskInfoFormState => ({
  isCompleted: Boolean(task?.isCompleted),
  performerIds: task?.performerIds ?? [],
  deadline: normalizeTaskDeadline(task?.deadline),
})

export const buildTaskInfoUpdatePayload = (
  current: TaskInfoFormState,
  snapshot: TaskInfoFormState,
): UpdateTaskTrackerTaskRequest => {
  const payload: UpdateTaskTrackerTaskRequest = {}

  if (current.isCompleted !== snapshot.isCompleted) {
    payload.is_completed = current.isCompleted
  }

  if (!areTaskPerformerIdsEqual(current.performerIds, snapshot.performerIds)) {
    payload.performer_ids = current.performerIds
  }

  if (current.deadline !== snapshot.deadline) {
    payload.deadline = current.deadline
  }

  return payload
}

/**
 * Payload для update при смене `is_completed`.
 * Бэк валидирует обязательные стикеры по телу запроса, а не по уже
 * сохранённым значениям — поэтому прокидываем текущие значения.
 */
export const buildTaskStatusUpdatePayload = (
  task: Pick<TaskTrackerBoardCard, 'deadline' | 'performerIds' | 'stickers'>,
  isCompleted: boolean,
): UpdateTaskTrackerTaskRequest => {
  const payload: UpdateTaskTrackerTaskRequest = {
    is_completed: isCompleted,
  }

  const deadline = normalizeTaskDeadline(task.deadline)
  if (deadline) {
    payload.deadline = deadline
  }

  if (task.performerIds.length > 0) {
    payload.performer_ids = [...task.performerIds]
  }

  const fieldValues: CreateTaskTrackerTaskFieldValue[] = []

  for (const sticker of task.stickers) {
    if (!sticker.isRequired) continue
    if (
      isTaskTrackerSystemDatetimeStickerFieldType(
        sticker.fieldType,
        sticker.systemKey,
      )
    ) {
      continue
    }
    if (
      sticker.fieldType === 'deadline' ||
      sticker.systemKey === 'deadline' ||
      sticker.fieldType === 'performer' ||
      sticker.systemKey === 'performer'
    ) {
      continue
    }

    const raw = sticker.value?.trim()
    if (!raw) continue

    if (sticker.fieldType === 'number') {
      const numericValue = Number(raw)
      if (!Number.isFinite(numericValue)) continue
      fieldValues.push({
        field_setting_id: sticker.fieldSettingId,
        value: numericValue,
      })
      continue
    }

    fieldValues.push({
      field_setting_id: sticker.fieldSettingId,
      value: raw,
    })
  }

  if (fieldValues.length > 0) {
    payload.field_values = fieldValues
  }

  return payload
}
